import { createContext, useCallback, useContext, useMemo } from 'react';

/**
 * Réglages d'application partagés : paramètres d'intégration, clés de service,
 * préférences d'entreprise.
 *
 * <p>Ces réglages sont portés par les paramètres de la société, donc persistés en
 * base : ils survivent à un changement d'appareil et valent pour tous les
 * utilisateurs de l'entreprise, ce qui n'était pas le cas lorsqu'ils vivaient
 * dans le navigateur.
 */
const ReglagesContext = createContext({
  reglages: {},
  reglage: () => '',
  setReglage: () => {},
  reglageJson: (_cle, defaut) => defaut,
  setReglageJson: () => {},
});

/**
 * Fournit les réglages d'application à l'arbre de composants.
 *
 * @param {object} props propriétés du composant
 * @param {object|null} props.soc paramètres de la société
 * @param {Function} props.setSoc mise à jour des paramètres de la société
 * @param {import('react').ReactNode} props.children arbre à envelopper
 * @returns {import('react').ReactElement} le fournisseur de contexte
 */
export function ReglagesProvider({ soc, setSoc, children }) {
  const reglages = useMemo(() => soc?.parametres ?? {}, [soc]);

  const reglage = useCallback(
    (cle, defaut = '') => {
      const valeur = reglages[cle];
      return valeur === undefined || valeur === null ? defaut : valeur;
    },
    [reglages],
  );

  const setReglage = useCallback(
    (cle, valeur) => {
      setSoc((precedent) => {
        if (!precedent) return precedent;
        const parametres = { ...(precedent.parametres ?? {}) };
        if (valeur === null || valeur === undefined || valeur === '') {
          delete parametres[cle];
        } else {
          parametres[cle] = String(valeur);
        }
        return { ...precedent, parametres };
      });
    },
    [setSoc],
  );

  /**
   * Lit un réglage qui porte une structure plutôt qu'une valeur simple.
   *
   * Les paramètres sont un sac de chaînes : tout ce qui n'en est pas une y est
   * écrit en JSON. Un contenu illisible — réglage écrit par une version plus
   * ancienne, ou tronqué — rend le défaut au lieu de faire échouer le rendu de
   * l'écran qui le demande.
   */
  const reglageJson = useCallback(
    (cle, defaut) => {
      const brut = reglages[cle];
      if (brut === undefined || brut === null || brut === '') {
        return defaut;
      }
      try {
        return JSON.parse(brut);
      } catch (e) {
        return defaut;
      }
    },
    [reglages],
  );

  const setReglageJson = useCallback(
    (cle, valeur) => {
      setReglage(cle, valeur === null || valeur === undefined ? '' : JSON.stringify(valeur));
    },
    [setReglage],
  );

  // La société elle-même : la lecture d'une facture reçue ne la prend pas
  // pour le fournisseur, elle qui en est le destinataire.
  const valeur = useMemo(
    () => ({ reglages, reglage, setReglage, reglageJson, setReglageJson, societe: soc }),
    [reglages, reglage, setReglage, reglageJson, setReglageJson, soc],
  );

  return <ReglagesContext.Provider value={valeur}>{children}</ReglagesContext.Provider>;
}

/**
 * Accède aux réglages d'application.
 *
 * @returns {{reglages: object, reglage: Function, setReglage: Function,
 *            reglageJson: Function, setReglageJson: Function, societe: object|null}}
 *          les réglages et leurs accesseurs
 */
export function useReglages() {
  return useContext(ReglagesContext);
}
